"use client"

import { ChevronUp, Trash2, Users } from "lucide-react"
import { format } from "date-fns"
import { observer } from "mobx-react-lite"
import { useState } from "react"

import { AuthUserButton } from "@/app/components/common/AuthUserButton"
import { Button } from "@/components/ui/button"
import LoginModal from "../screens/auth/LoginModal"
import { OnboardingModal } from "../screens/auth/OnboardingModal"
import { Separator } from "@/components/ui/separator"
import {
  checkGroupAvailability,
  memberDuration,
  memberTotal,
  submitGroupBooking,
  validateGroupServices,
  validateGroupTimes,
  validateGuests,
} from "@/lib/appointments/group-booking"
import { getApiErrorMessage } from "@/lib/utils/api-error"
import { cn } from "@/lib/utils"
import { toastUtils } from "@/utils/toast-utils"
import { useStore } from "@/app/components/common/store.provider"
import { guestLabel } from "./GroupGuestTabs"

function formatDuration(mins: number): string {
  if (mins < 60) return `${mins} mins`
  const h = Math.floor(mins / 60)
  const m = mins % 60
  if (m === 0) return `${h} hr${h > 1 ? "s" : ""}`
  return `${h} hr${h > 1 ? "s" : ""}, ${m} mins`
}

function toPriceLabel(price: number): string {
  return new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    maximumFractionDigits: 2,
  }).format(price)
}

type GroupAppointmentCartProps = {
  onCreated?: () => void
}

export const GroupAppointmentCart = observer(function GroupAppointmentCart({
  onCreated,
}: GroupAppointmentCartProps) {
  const { appointmentStore, authStore } = useStore()
  const [loginOpen, setLoginOpen] = useState(false)
  const [onboardingOpen, setOnboardingOpen] = useState(false)
  const [mobileExpanded, setMobileExpanded] = useState(false)

  const members = appointmentStore.groupMembers
  const stepId = appointmentStore.currentStepId
  const isCustom = appointmentStore.groupIsCustomBooking
  const isLoading = appointmentStore.isLoading
  const availability = appointmentStore.groupAvailability
  const isAuthenticated = authStore.isAuthenticated
  const total = appointmentStore.groupTotalPrice
  const totalDuration = appointmentStore.groupTotalDuration

  const profile = authStore.user?.profile
  const needsOnboarding =
    !profile ||
    !profile.firstName?.trim() ||
    !profile.lastName?.trim() ||
    !profile.phone?.trim()

  const requireAccount = (): boolean => {
    if (!isAuthenticated) {
      setLoginOpen(true)
      return false
    }
    if (needsOnboarding) {
      setOnboardingOpen(true)
      return false
    }
    return true
  }

  const advanceFromTime = async () => {
    const check = validateGroupTimes(appointmentStore)
    if (!check.valid) {
      toastUtils.error("Incomplete", check.message ?? "Complete the schedule first.")
      return
    }
    if (!requireAccount()) return
    if (isCustom) {
      appointmentStore.nextStep()
      return
    }

    appointmentStore.setIsLoading(true)
    try {
      const result = await checkGroupAvailability(
        appointmentStore,
        authStore.user?.id ? Number(authStore.user.id) : undefined,
      )
      if (!result.allFit) {
        toastUtils.error(
          "Schedule conflict",
          `${result.fittingCount} of ${result.partySize} guests fit. Review the suggestions on the next screen.`,
        )
      }
      appointmentStore.nextStep()
    } catch (error) {
      toastUtils.error(
        "Availability Check Failed",
        getApiErrorMessage(error, "Could not check availability."),
      )
    } finally {
      appointmentStore.setIsLoading(false)
    }
  }

  const submit = async () => {
    const organizerUserId = authStore.user?.id
    if (typeof organizerUserId !== "number") {
      setLoginOpen(true)
      return
    }

    appointmentStore.setIsLoading(true)
    try {
      await submitGroupBooking({
        store: appointmentStore,
        organizerUserId,
        organizerEmail: authStore.user?.email,
      })
      toastUtils.success(
        isCustom ? "Group Request Submitted" : "Group Booked",
        isCustom
          ? "Your group request is awaiting salon approval."
          : "Your group booking has been confirmed.",
      )
      appointmentStore.reset()
      onCreated?.()
    } catch (error) {
      const message = getApiErrorMessage(error, "Could not complete the group booking.")
      if (message.toLowerCase().includes("redirecting to paystack")) return
      toastUtils.error("Group Booking Failed", message)
    } finally {
      appointmentStore.setIsLoading(false)
    }
  }

  const handleContinue = () => {
    if (stepId === "guests") {
      const check = validateGuests(appointmentStore)
      if (!check.valid) {
        toastUtils.error("Incomplete", check.message ?? "Add your guests first.")
        return
      }
      appointmentStore.nextStep()
      return
    }

    if (stepId === "services") {
      const check = validateGroupServices(appointmentStore)
      if (!check.valid) {
        toastUtils.error("Incomplete", check.message ?? "Pick services for every guest.")
        return
      }
      appointmentStore.nextStep()
      return
    }

    if (stepId === "time") {
      void advanceFromTime()
      return
    }

    void submit()
  }

  const continueDisabled =
    isLoading ||
    (stepId === "guests" && members.length === 0) ||
    (stepId === "services" && members.some((m) => m.services.length === 0)) ||
    (stepId === "time" && !isCustom && members.some((m) => !m.startTime)) ||
    (stepId === "confirm" &&
      (!appointmentStore.acceptedTerms || (!isCustom && !availability?.allFit)))

  const continueLabel = isLoading
    ? stepId === "confirm"
      ? "Confirming…"
      : "Loading…"
    : stepId === "confirm"
      ? isCustom
        ? "Submit request"
        : "Confirm"
      : "Continue"

  return (
    <div className="flex h-full flex-col">
      <div
        className={cn(
          "min-h-0 flex-col space-y-3 overflow-y-auto scroll-smooth scrollbar-thin p-3 md:p-4",
          mobileExpanded ? "flex max-h-[40vh]" : "hidden",
          "lg:flex lg:max-h-none lg:flex-1",
        )}
      >
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-foreground">Your party</h3>
          <button
            type="button"
            className="inline-flex min-h-11 items-center gap-1 px-2 text-sm text-muted-foreground lg:hidden"
            onClick={() => setMobileExpanded(false)}
          >
            Hide
            <ChevronUp className="h-4 w-4" />
          </button>
        </div>

        {appointmentStore.groupDate ? (
          <div className="rounded-lg border bg-muted/50 p-3 text-sm">
            <p className="font-medium text-foreground">Appointment day</p>
            <p className="mt-1 text-muted-foreground">
              {format(new Date(`${appointmentStore.groupDate}T12:00:00`), "EEEE, MMM d")}
            </p>
          </div>
        ) : null}

        {members.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center text-center">
            <div className="mb-6 flex items-center justify-center rounded-full border p-4">
              <Users className="h-10 w-10" />
            </div>
            <h2 className="text-xl font-bold">No guests yet</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Add everyone you are booking for to get started.
            </p>
          </div>
        ) : null}

        {members.map((member, index) => {
          const isActive = member.localId === appointmentStore.activeMember?.localId
          const duration = memberDuration(member)
          const fits = availability?.members.find((m) => m.memberIndex === index)?.fits

          return (
            <div
              key={member.localId}
              className={cn(
                "rounded-lg border p-3 text-sm transition-colors",
                isActive ? "border-primary" : "border-border",
              )}
            >
              <div className="flex items-start justify-between gap-2">
                <button
                  type="button"
                  className="min-w-0 flex-1 text-left"
                  onClick={() => appointmentStore.setActiveGroupMember(member.localId)}
                >
                  <p className="truncate font-medium text-foreground">
                    {guestLabel(member, index)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {member.services.length} service
                    {member.services.length === 1 ? "" : "s"}
                    {duration > 0 ? ` · ${formatDuration(duration)}` : ""}
                  </p>
                </button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 shrink-0 text-muted-foreground hover:text-destructive"
                  aria-label={`Remove ${guestLabel(member, index)}`}
                  onClick={() => appointmentStore.removeGroupMember(member.localId)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>

              {member.services.length > 0 ? (
                <ul className="mt-2 space-y-1">
                  {member.services.map((service) => (
                    <li
                      key={service.id}
                      className="flex items-start justify-between gap-2 text-muted-foreground"
                    >
                      <span className="min-w-0 truncate">{service.name}</span>
                      <span className="shrink-0">{toPriceLabel(service.price)}</span>
                    </li>
                  ))}
                </ul>
              ) : null}

              <div className="mt-2 flex items-center justify-between gap-2">
                <span className="text-xs text-muted-foreground">
                  {isCustom
                    ? "Salon will schedule"
                    : member.startTime || "No time picked"}
                </span>
                <span className="font-semibold text-foreground">
                  {toPriceLabel(memberTotal(member))}
                </span>
              </div>

              {availability && fits === false ? (
                <p className="mt-2 rounded-md bg-destructive/10 px-2 py-1 text-xs text-destructive">
                  Does not fit at this time
                </p>
              ) : null}
            </div>
          )
        })}

        {isCustom ? (
          <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-900 dark:text-amber-100">
            Custom request enabled. The salon will schedule your whole party and confirm.
          </div>
        ) : null}

        {stepId === "confirm" && !isCustom ? (
          <div className="space-y-2 rounded-lg border p-3 text-sm">
            <p className="font-medium text-foreground">Payment summary</p>
            <div className="flex justify-between text-muted-foreground">
              <span>
                {members.length} guest{members.length === 1 ? "" : "s"}
              </span>
              <span>{toPriceLabel(total)}</span>
            </div>
            <Separator />
            <div className="flex justify-between font-semibold text-foreground">
              <span>Total due</span>
              <span>{toPriceLabel(total)}</span>
            </div>
          </div>
        ) : null}
      </div>

      <div className="shrink-0 space-y-2 border-t border-border bg-background p-3 md:p-4">
        <button
          type="button"
          className="flex min-h-11 w-full items-center justify-between text-left text-sm lg:hidden"
          onClick={() => setMobileExpanded((open) => !open)}
        >
          <span className="font-medium text-foreground">
            {members.length} guest{members.length === 1 ? "" : "s"} in your party
          </span>
          <ChevronUp
            className={cn(
              "h-4 w-4 text-muted-foreground transition-transform",
              mobileExpanded ? "" : "rotate-180",
            )}
          />
        </button>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-muted-foreground">
            {totalDuration > 0
              ? `Total: ${formatDuration(totalDuration)} · ${toPriceLabel(total)}`
              : `Total: ${toPriceLabel(total)}`}
          </p>
          <Button
            variant="default"
            size="default"
            className="w-full rounded-full py-5 sm:w-auto sm:min-w-36"
            onClick={handleContinue}
            disabled={continueDisabled}
          >
            {continueLabel}
          </Button>
        </div>
        <AuthUserButton
          color="brand"
          showLabel="always"
          showSignInAction
          onClick={() => {
            if (!isAuthenticated) setLoginOpen(true)
          }}
        />
      </div>

      <LoginModal
        open={loginOpen}
        onOpenChange={setLoginOpen}
        onSuccess={() => setLoginOpen(false)}
        shouldCloseOnSuccess
        hasTrigger={false}
      />
      <OnboardingModal
        open={onboardingOpen}
        onOpenChange={setOnboardingOpen}
        onSuccess={() => setOnboardingOpen(false)}
      />
    </div>
  )
})
